import React from "react"

import Button from "legacy/components/shared/Button"
import RichText from "legacy/components/shared/RichText/RichText"
import { ColumnType } from "types/shared-components"
import { Element, Image } from "legacy/components/shared/base"
import { LinkSwitch } from "legacy/components/Redesign/Link"
import { Maybe } from "types/Maybe"
import { Title } from "legacy/components/Redesign/Title/Title"

export type ColumnProps = {
  data: Maybe<ColumnType>
}

const setImageHeight = (image_size: string): string => {
  let imageHeight = ""
  switch (image_size) {
    case "xxlarge":
      imageHeight = "308px"
      break
    case "xlarge":
      imageHeight = "160px"
      break
    case "large":
      imageHeight = "120px"
      break
    default:
      imageHeight = "96px"
  }
  return imageHeight
}

export const Column: React.FC<ColumnProps> = ({
  data,
}): React.ReactElement | null => {
  const { items, primary } = data
  if (!items || !primary) return null

  const {
    align_columns,
    button_style,
    button_text,
    button_type,
    button_url,
    column_slice_title,
    title_alignment,
    title_size,
  } = primary

  const title = column_slice_title?.text

  let amountOfColumns = "1fr 1fr 1fr"

  if (items.length === 2) {
    amountOfColumns = "1fr 1fr"
  } else if (items.length === 1) {
    amountOfColumns = "1fr"
  }

  return (
    <Element px="sm">
      <Element maxWidth="contentWidth" mx="auto" px="sm" py={["lg", "xlg"]}>
        {title && (
          <Title
            mb={["md", "lg"]}
            textAlign={title_alignment === "Left" ? "left" : "center"}
            title={title}
            titleSize={title_size}
          />
        )}
        <Element
          display={["block", "grid"]}
          gridTemplateColumns={["none", amountOfColumns]}
          gridGap="sm"
          textAlign={align_columns ? align_columns : "left"}
        >
          {items.map((item, index) => {
            const {
              button_text,
              button_url,
              column_intro,
              column_title,
              eyebrow,
              image,
              image_size,
            } = item

            const imageHeight = image?.dimensions?.height
            const imageWidth = image?.dimensions?.width

            return (
              <Element key={index} mb={["md", "0"]}>
                {image?.alt && image?.url && (
                  <Element mb={["md", "lg"]}>
                    <Image
                      aspectRatio={`${imageWidth} / ${imageHeight}`}
                      imageData={{
                        alt: image.alt,
                        url: image.url,
                      }}
                      height={setImageHeight(image_size)}
                      width="auto"
                    />
                  </Element>
                )}
                {eyebrow && (
                  <Element
                    color="offBlack"
                    fontFamily="martel"
                    fontSize="16px"
                    fontWeight="600"
                    lineHeight="28px"
                    letterSpacing="wide"
                    mb="xsm"
                    textTransform="uppercase"
                  >
                    {eyebrow}
                  </Element>
                )}
                {column_title?.text && (
                  <Title
                    color="offBlack"
                    fontFamily="prospectus"
                    fontSize={["20", "24px"]}
                    fontWeight="700"
                    lineHeight={["25px", "30px"]}
                    mb="sm"
                    title={column_title?.text}
                    titleSize={title_size}
                  />
                )}
                {column_intro?.richText && (
                  <RichText
                    color="offBlack"
                    fontFamily="martel"
                    fontSize={["17px", "18px"]}
                    lineHeight={["28px", "32px"]}
                    mb={["md", "lg"]}
                    render={column_intro?.richText}
                  />
                )}
                {button_text && button_url?.url && (
                  <LinkSwitch
                    linkType={button_type}
                    buttonType={button_type}
                    mt={button_style === "secondary" ? "sm" : ["md", "lg"]}
                    url={button_url}
                    variant={button_style}
                  >
                    {button_text}
                  </LinkSwitch>
                )}
              </Element>
            )
          })}
        </Element>
        {button_text && button_url?.url && (
          <Element mt="lg" textAlign="center">
            <Button
              as="a"
              display="inline-block"
              fontSize={["16", "text.button"]}
              href={button_url.url}
              width="100%"
              px="15"
              py="20"
              variant="primarySky"
            >
              {button_text}
            </Button>
          </Element>
        )}
      </Element>
    </Element>
  )
}
